import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Trash2, Check, X, Bookmark, Search } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useMeta, inkHex } from "@/lib/meta";
import { StampSvg } from "@/components/StampSvg";
import { sizeLabel } from "@/components/ProductThumb";

function StampCard({ s, onOpen, onRename, onDelete }) {
  const meta = useMeta();
  const [edit, setEdit] = useState(false);
  const [name, setName] = useState(s.name);
  const save = () => { if (name.trim()) { onRename(name.trim()); setEdit(false); } };
  return (
    <div className="rounded-lg border border-slate-200 bg-white flex items-center gap-3 p-2 pr-3 hover:border-brand-navy transition-colors" data-testid={`my-stamp-${s.id}`}>
      <button onClick={onOpen} title="Apri nell'editor" data-testid={`my-stamp-thumb-${s.id}`}
        className="ink-grid w-28 h-16 shrink-0 rounded flex items-center justify-center overflow-hidden">
        <StampSvg layout={s.layout} color={inkHex(meta, s.ink_color)} scale={Math.min(104 / s.width_mm, 56 / s.height_mm)} />
      </button>
      <div className="min-w-0 flex-1">
        {edit ? (
          <div className="flex items-center gap-1">
            <input autoFocus data-testid={`my-stamp-rename-input-${s.id}`} value={name} maxLength={120} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()}
              className="h-7 min-w-0 flex-1 px-2 rounded border border-slate-300 text-sm" />
            <button data-testid={`my-stamp-rename-save-${s.id}`} onClick={save} className="w-7 h-7 rounded bg-brand-navy text-white flex items-center justify-center"><Check className="w-3.5 h-3.5" /></button>
            <button onClick={() => { setEdit(false); setName(s.name); }} className="w-7 h-7 rounded border flex items-center justify-center"><X className="w-3.5 h-3.5" /></button>
          </div>
        ) : (
          <button onClick={() => setEdit(true)} title="Rinomina" className="font-semibold text-sm truncate block max-w-full text-left hover:text-brand-navy" data-testid={`my-stamp-name-${s.id}`}>{s.name}</button>
        )}
        <div className="text-[11px] text-slate-500 truncate">{s.product_name} · <span className="font-mono">{sizeLabel(s)}</span> · {fmtDate(s.updated_at)}</div>
      </div>
      <button data-testid={`my-stamp-open-${s.id}`} onClick={onOpen} className="press h-8 px-3 rounded-md bg-brand-navy text-white text-xs font-semibold flex items-center gap-1 shrink-0"><Pencil className="w-3.5 h-3.5" /> Apri</button>
      <button data-testid={`my-stamp-delete-${s.id}`} onClick={onDelete} title="Elimina" className="press w-8 h-8 rounded-md border border-red-200 bg-red-50 text-red-600 flex items-center justify-center shrink-0 hover:bg-red-600 hover:text-white"><Trash2 className="w-3.5 h-3.5" /></button>
    </div>
  );
}

export function MyStamps() {
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  useEffect(() => { api.get("/my/stamps").then((r) => setList(r.data)).catch(() => setList([])); }, []);
  const open = (s) => navigate(`/timbro/${s.slug}`, { state: { reorder: { design: s.design, ink_color: s.ink_color, quantity: 1, pads: 0 }, savedName: s.name } });
  const rename = (s, name) => api.patch(`/my/stamps/${s.id}`, { name }).then(() => { setList((l) => l.map((x) => (x.id === s.id ? { ...x, name } : x))); toast.success("Nome aggiornato"); }).catch((e) => toast.error(errMsg(e)));
  const del = (s) => {
    if (!window.confirm(`Eliminare "${s.name}" dai tuoi timbri?`)) return;
    api.delete(`/my/stamps/${s.id}`).then(() => setList((l) => l.filter((x) => x.id !== s.id))).catch((e) => toast.error(errMsg(e)));
  };
  const shown = (list || []).filter((s) => !q.trim() || `${s.name} ${s.product_name}`.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <section className="space-y-3" data-testid="my-stamps">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h2 className="text-2xl font-bold uppercase flex items-center gap-2"><Bookmark className="w-5 h-5 text-brand-navy" /> I miei timbri {list?.length > 0 && <span className="font-mono text-sm text-slate-500">({list.length})</span>}</h2>
        {list?.length > 4 && (
          <div className="relative w-full sm:w-64">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-400" />
            <input data-testid="my-stamps-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome…" className="w-full h-8 pl-8 pr-2 rounded-md border border-slate-200 text-sm" />
          </div>
        )}
      </div>
      {list === null ? <p className="text-sm text-slate-500">Caricamento…</p> : !list.length ? (
        <p className="text-sm text-slate-500 rounded-lg border border-dashed border-slate-300 p-4" data-testid="my-stamps-empty">
          Nessun timbro salvato. Nell'editor usa <b>"Salva nei miei timbri"</b> oppure spunta la casella nel carrello: li ritroverai qui, visibili solo a te.
        </p>
      ) : (
        <div className="grid md:grid-cols-2 gap-2" data-testid="my-stamps-list">
          {shown.map((s) => <StampCard key={s.id} s={s} onOpen={() => open(s)} onRename={(n) => rename(s, n)} onDelete={() => del(s)} />)}
          {!shown.length && <p className="text-sm text-slate-500">Nessun timbro con questo nome.</p>}
        </div>
      )}
    </section>
  );
}
